import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Header } from '../../common/components/Header';
import { Footer } from '@/scopes/common/components';
import { SummerContent } from '@/scopes/common/components/Footer/SummerContent';
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary';
import { Custom500 } from '@/pages/500';
import Layout from '@/scopes/common/components/Layout/Layout';
import Gallery from './components/Gallery';
import { styled } from 'styled-components';
import PDPHeading from './components/PDPHeading';
import Description from './components/Description';
import RoomTypes from './components/RoomTypes';
import Facilities from './components/Facilities';
import PracticalInformation from './components/PracticalInformation';
import UserReviews from './components/UserReviews';
import { Spacer, tk, verticalGap } from '@sunwebgroup/ui-components';
import DestinationSection from './components/DestinationSection';
import { PriceTable } from '@/react-pages/Prices/PriceTable';
import { useLayoutEffect, useRef } from 'react';
import PromotedPrice from '../../../react-pages/Prices/PromotedPrice';
import RecommendedAccos from '@/scopes/products/pages/components/RecommendedAccos/RecommendedAccos';
import AnchorMenu, { AnchorHref, AnchorTarget } from './components/AnchorMenu';
import { usePDPStructuredMarkup } from '@/scopes/products/pages/hooks';
import { PDPDependenciesProvider } from '@/scopes/products/infrastructure/dependencies';
import { pdpStore } from '@/scopes/products/infrastructure/store';
import { isClient } from '@/utilities';
import { UrgencyBanner } from '@/react-pages/Prices/UrgencyBanner';
import NextHead from 'next/head';
import { useCMSPageField } from '@/cms/fields';
import { usePricesStore } from '@/infrastructure/prices/usePricesStore';

const HeroSection = styled(Layout)`
  margin-top: 20px;
  margin-bottom: ${tk('spacing_md')};

  @media ${tk('mq_tablet')} {
    margin-bottom: ${tk('spacing_lg')};
  }
`;

const LayoutWithSidebar = styled.div`
  display: flex;
  flex-direction: column-reverse;
  margin-bottom: ${tk('spacing_md')};

  @media ${tk('mq_tablet')} {
    margin-bottom: ${tk('spacing_lg')};
  }

  @media ${tk('mq_desktop_lg')} {
    flex-direction: row;
    margin-bottom: ${tk('spacing_4xl')};
  }
`;

const LayoutFullWidth = styled.div``;

const RecommendedAccosLayout = styled(LayoutFullWidth)`
  background-color: ${tk('color_semantic_bg_subdued')};
  overflow-x: hidden;

  @media ${tk('mq_tablet')} {
    background-color: unset;
  }
`;

const DetailSection = styled.div`
  flex: 1;
  ${verticalGap('spacing_lg')} @media ${tk('mq_desktop_lg')} {
    ${verticalGap('spacing_xl')}
    & > * {
      margin-right: ${tk('spacing_2xl')};
    }
  }
`;

const PromotedPriceSection = styled.div`
  flex: 0.3;

  @media ${tk('mq_desktop_lg')} {
    background-color: ${tk('color_semantic_bg_subdued')};
    border-radius: ${tk('radius_large')};
    padding: ${tk('spacing_md')};
    padding-top: 0;
    position: relative;

    & > * {
      position: sticky;
      top: ${tk('spacing_md')};
    }
  }
`;

const PriceTableSection = styled(Layout)`
  display: flex;
  flex-direction: column;
`;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
    },
  },
});

function AccomodationPageFullReact() {
  const { showPriceTable, setShowPriceTable } = usePricesStore();
  const metaData = useCMSPageField('PDP.productMetadata');

  usePDPStructuredMarkup();

  const legacyBanner = isClient() ? document.querySelector('.discount-banner--react-compatible') : false;

  const scrollRef = useRef<HTMLDivElement>(null);

  const promotedPriceClick = () => {
    setShowPriceTable(true);
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useLayoutEffect(() => {
    const url = window.location.search;
    const params = new URLSearchParams(url);
    const showPriceTableParam = params.get('focusPriceTable');
    if (showPriceTableParam && showPriceTableParam?.toLowerCase() === 'true') {
      promotedPriceClick();
    }
  }, []);

  return (
    <>
      {metaData && (
        <NextHead>
          {metaData.metaTitle && <title>{metaData.metaTitle}</title>}
          {metaData.metaDescription && <meta name="description" content={metaData.metaDescription} />}
        </NextHead>
      )}
      <PDPDependenciesProvider dependencies={{ pdpStore }}>
        <QueryClientProvider client={queryClient}>
          <Header />
          <NewRelicErrorBoundary scope="acco-page" fallback={<Custom500 brand="sunweb" />}>
            <HeroSection>
              <Gallery isFullReact />
              <PDPHeading isFullReact />
              {!legacyBanner && <UrgencyBanner />}
            </HeroSection>
            <div ref={scrollRef}>
              {!showPriceTable && (
                <>
                  <Layout>
                    <LayoutWithSidebar>
                      <DetailSection>
                        <AnchorMenu />
                        <AnchorTarget anchorHref={AnchorHref.General}>
                          <Description />
                        </AnchorTarget>
                        <AnchorTarget anchorHref={AnchorHref.Roomtypes}>
                          <RoomTypes />
                        </AnchorTarget>
                        <AnchorTarget anchorHref={AnchorHref.Facilities}>
                          <Facilities />
                        </AnchorTarget>
                        <AnchorTarget anchorHref={AnchorHref.PracticalInfo}>
                          <PracticalInformation />
                        </AnchorTarget>
                        <AnchorTarget anchorHref={AnchorHref.Reviews}>
                          <UserReviews />
                        </AnchorTarget>
                        <AnchorTarget anchorHref={AnchorHref.Destination}>
                          <DestinationSection />
                        </AnchorTarget>
                      </DetailSection>
                      <PromotedPriceSection>
                        <PromotedPrice onClick={promotedPriceClick} isFullReact />
                      </PromotedPriceSection>
                    </LayoutWithSidebar>
                  </Layout>
                  <RecommendedAccosLayout>
                    <Layout>
                      <RecommendedAccos />
                    </Layout>
                  </RecommendedAccosLayout>
                </>
              )}
              {showPriceTable && (
                <PriceTableSection>
                  <PriceTable onClickBack={() => setShowPriceTable(false)} />
                </PriceTableSection>
              )}
            </div>
          </NewRelicErrorBoundary>
          <Spacer $size={64} />
          <Footer>
            <SummerContent />
          </Footer>
          {process.env.NODE_ENV === 'development' && <ReactQueryDevtools client={queryClient} />}
        </QueryClientProvider>
      </PDPDependenciesProvider>
    </>
  );
}

export default AccomodationPageFullReact;
